import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  ProjectDto,
  ProjectType,
  ProjectStatus,
  ProjectCreateInput,
} from '@cc/types';

const TYPES: ProjectType[] = ['BUSINESS', 'PROPERTY', 'DECISION', 'CREATIVE', 'PERSONAL'];
const STATUSES: ProjectStatus[] = ['ACTIVE', 'DORMANT', 'COMPLETE'];

export function SettingsProjectsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: qk.projects,
    queryFn: () => api.listProjects(),
  });

  const [editing, setEditing] = useState<ProjectDto | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Settings
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Project registry</h1>
            <p className="text-sm text-muted-foreground">
              The disambiguator and project query both read from this. Keep aliases and
              descriptions tight: they drive routing accuracy.
            </p>
          </div>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> New project
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {data?.projects.map((p) => (
                <li key={p.id} className="flex items-center gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium">{p.name}</p>
                      <Badge
                        variant={
                          p.status === 'ACTIVE'
                            ? 'success'
                            : p.status === 'DORMANT'
                              ? 'warning'
                              : 'muted'
                        }
                      >
                        {p.status.toLowerCase()}
                      </Badge>
                      <Badge variant="outline">{p.type.toLowerCase()}</Badge>
                    </div>
                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {p.description || 'No description.'}
                    </p>
                    {p.aliases.length > 0 && (
                      <p className="mt-1 truncate text-[11px] text-muted-foreground/70">
                        aliases: {p.aliases.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <DeleteButton project={p} />
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {editing && (
        <ProjectFormDialog
          mode="edit"
          project={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: qk.projects });
            void queryClient.invalidateQueries({ queryKey: qk.project(editing.id) });
            setEditing(null);
          }}
        />
      )}

      {creating && (
        <ProjectFormDialog
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: qk.projects });
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function DeleteButton({ project }: { project: ProjectDto }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const mutation = useMutation({
    mutationFn: () => api.deleteProject(project.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projects });
      toast.success(`Deleted ${project.name}.`);
      setOpen(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'has_activities') {
        toast.error('Project has activities. Set status to Complete or Dormant instead.');
      } else {
        toast.error('Delete failed.');
      }
      setOpen(false);
    },
  });
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <Trash2 className="h-3.5 w-3.5 text-destructive" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete project</DialogTitle>
            <DialogDescription>
              Permanently delete {project.name}? This can't be undone. Projects with linked
              activities can't be deleted, set them to Complete instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface FormDialogProps {
  mode: 'create' | 'edit';
  project?: ProjectDto;
  onClose: () => void;
  onSaved: () => void;
}

function ProjectFormDialog({ mode, project, onClose, onSaved }: FormDialogProps) {
  const [name, setName] = useState(project?.name ?? '');
  const [type, setType] = useState<ProjectType>(project?.type ?? 'BUSINESS');
  const [status, setStatus] = useState<ProjectStatus>(project?.status ?? 'ACTIVE');
  const [description, setDescription] = useState(project?.description ?? '');
  const [aliases, setAliases] = useState((project?.aliases ?? []).join(', '));
  const [keyEntities, setKeyEntities] = useState((project?.keyEntities ?? []).join(', '));
  const [saving, setSaving] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const input: ProjectCreateInput = {
        name: name.trim(),
        type,
        status,
        description: description.trim(),
        aliases: parseList(aliases),
        keyEntities: parseList(keyEntities),
      };
      if (mode === 'create') {
        await api.createProject(input);
        toast.success(`Created ${input.name}.`);
      } else if (project) {
        await api.updateProject(project.id, input);
        toast.success(`Updated ${input.name}.`);
      }
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Save failed.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New project' : `Edit ${project?.name}`}</DialogTitle>
          <DialogDescription>
            Edit notes from the project page. Here you control identity: name, type, aliases.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as ProjectType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t.toLowerCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as ProjectStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s.toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="2 to 3 sentences. The disambiguator uses this to pick a project for inbound messages."
              className="min-h-[80px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="aliases">Aliases (comma separated)</Label>
            <Input
              id="aliases"
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="the commercial, High Street, the airbnb reno"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="entities">Key entities (people, vendors, places)</Label>
            <Input
              id="entities"
              value={keyEntities}
              onChange={(e) => setKeyEntities(e.target.value)}
              placeholder="Pancho, Joshua Theunissen, Hepburn"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function parseList(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
